import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { difference } from 'underscore';
import { ALERT_TYPES } from '../components/general-components/alert-types';
import { showAlert } from '../components/general-components/alert-service';
import { setNotifyOnUpdate } from '../stores/actions/media';
import { MediaItem } from '../stores/reducers/media';
import { GreenroomState } from '../stores/types/types';
import { MEDIA_TYPES, MEDIA_UPDATED_EVENTS } from '../types/constants';
import { downloadFile } from '../utils/media';

const useMediaNotifications = () => {
	const dispatch = useDispatch();
	const embed = useSelector((state: GreenroomState) => state.SessionReducer.embed);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const stateMedia = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const mediaRefreshed = useSelector((state: GreenroomState) => state.MediaReducer.mediaRefreshed);
	const mediaNotifyOnUpdate = useSelector(
		(state: GreenroomState) => state.MediaReducer.notifyOnUpdate,
	);
	const mediaUpdateEventInfo = useSelector(
		(state: GreenroomState) => state.MediaReducer.updateEventInfo,
	);

	const [currentMedia, setCurrentMedia] = useState<MediaItem[]>([]);

	useEffect(() => {
		if (mediaRefreshed && currentMedia.length !== 0) {
			// look for changes and pop rename notification for new recordings
			const mediaDifference = difference(
				stateMedia.map((mediaItem) => mediaItem.id),
				currentMedia.map((mediaItem) => mediaItem.id),
			);

			if (mediaDifference.length === 0) {
				return;
			}

			const newMediaItems = stateMedia.filter((mediaItem) => (
				mediaDifference?.includes(mediaItem.id)
			));

			newMediaItems.forEach((newMediaItem: MediaItem) => {
				if (
					host
					&& newMediaItem.type === MEDIA_TYPES.HLS
					&& !newMediaItem.original_id // not trimmed file
				) {
					showAlert({
						type: ALERT_TYPES.MEDIA_UPLOADED,
						message: newMediaItem.id,	// passing id to save changes if edited
						description: newMediaItem.custom_name || newMediaItem.original_name,
						duration: 5000,
					});
				}
			});
		}

		setCurrentMedia(stateMedia);
	}, [mediaRefreshed]);

	useEffect(() => {
		if (!embed && mediaNotifyOnUpdate && mediaUpdateEventInfo && mediaRefreshed) {
			const editedMedia = stateMedia.find((_media) => _media.id === mediaUpdateEventInfo.mediaId);
			const nameOfMedia = editedMedia?.custom_name || editedMedia?.original_name || '';

			if (editedMedia
				&& mediaUpdateEventInfo.type === MEDIA_UPDATED_EVENTS.DOWNLOAD_READY
				&& user?.gr_producer
			) {
				let description = '';
				let downloadLink = '';

				if (editedMedia.type === MEDIA_TYPES.HLS && host) {
					description = `Your recording "${nameOfMedia}"${editedMedia?.media_data.description ? ` starting on "${editedMedia?.media_data.description}"` : ''} is ready for download`;
					downloadLink = editedMedia?.recording_download_link || '';
				}

				if (!description.length || !downloadLink.length) {
					return;
				}

				showAlert({
					message: '',
					description,
					duration: 5000,
					onClick: () => {
						downloadFile(downloadLink, nameOfMedia);
					},
				});

				dispatch(setNotifyOnUpdate(false));
			}
		}
	}, [mediaRefreshed, mediaNotifyOnUpdate, mediaUpdateEventInfo]);
};

export default useMediaNotifications;
